import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { IntlProvider } from "react-intl";
import "./assets/styles/app.scss";
import ProtectedPage from "./components/auth/ProtectedPage";
import { selectLanguage } from "./i18n/selectLanguage";
import { ToastContainer } from "react-toastify";
import store from "./store/store";
import { Provider } from "react-redux";

import "react-toastify/dist/ReactToastify.css";

import "@mantine/core/styles.css";
import {
  ColorSchemeScript,
  MantineProvider,
  mantineHtmlProps,
} from "@mantine/core";

import RegisterPage from "./components/account/register/RegisterPage";
import ApiKeyListPage from "./components/api-keys/list/ApiKeyListPage";
import FormListPage from "./components/form/list/FormListPage";
import EndpointListPage from "./components/endpoint/list/EndpointListPage";
import EndpointDetailsPage from "./components/endpoint/details/EndpointDetailsPage";
import FormDetailsPage from "./components/form/details/FormDetailsPage";
import FlowDetailsPage from "./components/flows/details/FlowDetailsPage";
import SharedFormPage from "./components/form/sharing/SharedFormPage";
import WorkspaceSettingsPage from "./components/workspace/WorkspaceSettingsPage";
import SettingsPage from "./components/account/SettingsPage";
import ForgotPasswordPage from "./components/account/forgot-password/ForgotPasswordPage";
import ResetPasswordPage from "./components/account/forgot-password/ResetPasswordPage";
import EmailConfirmedPage from "./components/account/register/email-confirmed/EmailConfirmedPage";
import FlowWaitlistPage from "./components/flows/list/FlowWaitlistPage";
import SharedFormResultPage from "./components/form/sharing/SharedFormResultPage";
import TeamPage from "./components/team/TeamPage";
import InvitationPage from "./components/account/invitation/InvitationPage";

import PromptListPageWrapper from "./components/prompt/list/PromptListPageWrapper";
import PromptDetailsPageWrapper from "./components/prompt/details/PromptDetailsPageWrapper";
import SubscriptionPage from "./components/account/SubscriptionPage";
import HubWelcomePage from "./components/hub/welcome/HubWelcomePage";
import HubModelsPage from "./components/hub/models/HubModelsPage";
import HubModelDetails from "./components/hub/model_details/HubModelDetails";
import HubJobDetailsPage from "./components/hub/job_details/HubJobDetailsPage";
import ChatbotListPage from "./components/chatbot/chatbots/ChatbotListPage";
import ChatbotDetailsPage from "./components/chatbot/chatbot-details/ChatbotDetailsPage";
import ChatbotWidgetPage from "./widgets/chatbot/content/ChatbotWidgetPage";
import McpServersPage from "./components/mcp-platform/servers/McpServersPage";
import McpServerDetailsPage from "./components/mcp-platform/server-details/McpServerDetailsPage";

import NewChatListPage from "./components/workspace/chats/list/ChatListPage";
import NewChatPage from "./components/workspace/chats/details/ChatPage";
import VendorKeyListPage from "./components/workspace/vendor-keys/VendorKeyListPage";
import IntegrationListPage from "./components/workspace/integrations/list/IntegrationListPage";
import NewPromptListPage from "./components/workspace/prompts/list/PromptListPage";
import NewPromptDetailsPage from "./components/workspace/prompts/details/PromptDetailsPage";
import ToolPluginListPage from "./components/workspace/tools/list/ToolPluginListPage";
import ToolPluginDetailsPage from "./components/workspace/tools/details/ToolPluginDetailsPage";
import NewFlowWaitlistPage from "./components/workspace/flows/list/FlowWaitlistPage";
import NewWorkspaceSettingsPage from "./components/workspace/NewWorkspaceSettingsPage";
import KBaseListPage from "./components/workspace/knowledge/list/KBaseListPage";
import KBaseDetailsPage from "./components/workspace/knowledge/details/KBaseDetailsPage";
import AssistantListPage from "./components/workspace/assistants/AssistantListPage";
import AssistantDetailsPage from "./components/workspace/assistants/details/AssistantDetailsPage";
import GatewayListPage from "./components/workspace/gateways/list/GatewayListPage";
import GatewayDetailsPage from "./components/workspace/gateways/details/GatewayDetailsPage";
import ProjectListPage from "./components/builder/list/ProjectListPage";
import NewBuilderProjectPage from "./components/builder/NewBuilderProjectPage";
import ProjectChatPage from "./components/builder/details/ProjectChatPage";
import TemplateCataloguePage from "./components/builder/templates/TemplateCataloguePage";
import TemplateDetailPage from "./components/builder/templates/TemplateDetailPage";

const App = () => {
  const { lang, resources } = selectLanguage();

  return (
    <IntlProvider locale={lang} messages={resources} defaultLocale="en">
      <MantineProvider
        defaultColorScheme="light"
        withGlobalStyles
        withNormalizeCSS
      >
        <Provider store={store}>
          <Router basename="/app">
            <Routes>
              <Route path="/" element={<ProtectedPage />}>
                <Route path="/" element={<NewChatListPage />} />
                <Route path="/ai/chatbots" element={<ChatbotListPage />} />
                <Route
                  path="/ai/chatbots/:chatbotId/:tabId"
                  element={<ChatbotDetailsPage />}
                />
                <Route path="/hub/start" element={<HubWelcomePage />} />
                <Route path="/hub/models" element={<HubModelsPage />} />
                <Route
                  path="/hub/models/:handle"
                  element={<HubModelDetails />}
                />
                <Route
                  path="/hub/models/:handle"
                  element={<HubModelDetails />}
                />
                <Route
                  path="/hub/models/:handle/jobs/:jobId"
                  element={<HubJobDetailsPage />}
                />
                <Route path="/account" element={<SettingsPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route
                  path="/w/:workspaceId/keys"
                  element={<ApiKeyListPage />}
                />
                <Route
                  path="/w/:workspaceId/settings"
                  element={<WorkspaceSettingsPage />}
                />

                <Route
                  path="/w/:workspaceId/prompts/:promptId"
                  element={<PromptDetailsPageWrapper />}
                />
                <Route
                  path="/prompts/:promptId"
                  element={<PromptDetailsPageWrapper area={true} />}
                />
                <Route
                  path="/w/:workspaceId/prompts/:promptId/:versionId"
                  element={<PromptDetailsPageWrapper />}
                />
                <Route
                  path="/prompts/:promptId/:versionId"
                  element={<PromptDetailsPageWrapper area={true} />}
                />

                <Route
                  path="/w/:workspaceId/prompts"
                  element={<PromptListPageWrapper />}
                />
                <Route
                  path="/prompts"
                  element={<PromptListPageWrapper area={true} />}
                />

                <Route
                  path="/w/:workspaceId/forms"
                  element={<FormListPage />}
                />
                <Route
                  path="/w/:workspaceId/forms/:formId"
                  element={<FormDetailsPage />}
                />

                <Route
                  path="/w/:workspaceId/endpoints/:endpointId"
                  element={<EndpointDetailsPage />}
                />
                <Route
                  path="/w/:workspaceId/endpoints"
                  element={<EndpointListPage />}
                />
                <Route
                  path="/w/:workspaceId/flows"
                  element={<FlowWaitlistPage />}
                />
                <Route
                  path="/w/:workspaceId/flows/:flowId"
                  element={<FlowDetailsPage />}
                />
                {/* MCP Servers */}
                <Route path="/mcp-servers" element={<McpServersPage />} />
                <Route
                  path="/mcp-servers/:serverId"
                  element={<McpServerDetailsPage />}
                />
                {/* New Workspace */}
                <Route
                  path="/workspaces/:workspaceId/chats"
                  element={<NewChatListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/chats/:folderId"
                  element={<NewChatListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/chats/:chatId/edit"
                  element={<NewChatPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/vendor-keys"
                  element={<VendorKeyListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/integrations"
                  element={<IntegrationListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/prompts"
                  element={<NewPromptListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/prompts/:promptId"
                  element={<NewPromptDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/prompts/:promptId/:versionId"
                  element={<NewPromptDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/tool-plugins"
                  element={<ToolPluginListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/tool-plugins/:toolId"
                  element={<ToolPluginDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/knowledge"
                  element={<KBaseListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/knowledge/:kbaseId/:tabId"
                  element={<KBaseDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/assistants"
                  element={<AssistantListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/assistants/:assistantId"
                  element={<AssistantDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/gateways"
                  element={<GatewayListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/gateways/:gatewayId"
                  element={<GatewayDetailsPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/flows"
                  element={<NewFlowWaitlistPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/settings"
                  element={<NewWorkspaceSettingsPage />}
                />
                {/* Builder */}
                <Route
                  path="/workspaces/:workspaceId/builder"
                  element={<ProjectListPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/builder/new"
                  element={<NewBuilderProjectPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/builder/templates"
                  element={<TemplateCataloguePage />}
                />
                <Route
                  path="/workspaces/:workspaceId/builder/templates/:templateUid"
                  element={<TemplateDetailPage />}
                />
                <Route
                  path="/workspaces/:workspaceId/builder/:chatId"
                  element={<ProjectChatPage />}
                />

                {/* / New Workspace */}
              </Route>

              {/* shared forms */}
              <Route
                path="/f/:workspaceId/:formId"
                element={<SharedFormPage />}
              />
              <Route
                path="/f/:workspaceId/:formId/:submissionId"
                element={<SharedFormResultPage />}
              />
              {/* account */}
              <Route
                path="/forgot-password/:tokenId"
                element={<ResetPasswordPage />}
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              <Route
                path="/register/email-confirmed"
                element={<EmailConfirmedPage />}
              />

              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/register/invitation/:invitationId"
                element={<InvitationPage />}
              />

              {/* Widgets */}
              <Route
                path="/widgets/chatbot/:chatbotId"
                element={<ChatbotWidgetPage />}
              />

              <Route
                path="*"
                element={
                  <main style={{ padding: "1rem 2rem" }}>
                    <h1>Page not found</h1>
                  </main>
                }
              />
            </Routes>
          </Router>
        </Provider>
      </MantineProvider>
      <ToastContainer position="top-right" />
    </IntlProvider>
  );
};

export default App;
